"use client";

import React from "react";

import { trackPhoneClickConversion } from "@/components/utilities/googleAdsTracking";

export interface WGoogleMapAddressSectionProps {
  googleMapLink?: string;
  addressBox?: {
    title?: string;
    cityName?: string;
    address?: string;
    phoneNumber?: string;
    email?: string;
    workingHours?: string;
  };
}

/** Returns true if the URL looks like Google Maps and can be tried in an iframe. */
function isGoogleMapsUrl(url: string): boolean {
  try {
    const u = url.trim().toLowerCase();
    return (
      u.includes("google.com/maps") ||
      u.includes("goo.gl/maps") ||
      u.includes("maps.app.goo.gl") ||
      u.includes("maps.google.com")
    );
  } catch {
    return false;
  }
}

export default function WGoogleMapAddressSection({
  googleMapLink,
  addressBox,
}: WGoogleMapAddressSectionProps) {
  const mapUrl =
    googleMapLink != null && String(googleMapLink).trim() !== ""
      ? String(googleMapLink).trim()
      : "";
  const box = addressBox ?? {};
  const hasBox =
    box.title != null ||
    box.cityName != null ||
    (box.address != null && String(box.address).trim() !== "") ||
    box.phoneNumber != null ||
    box.email != null ||
    (box.workingHours != null && String(box.workingHours).trim() !== "");

  if (!mapUrl && !hasBox) return null;

  const useEmbed = mapUrl && isGoogleMapsUrl(mapUrl);
  const embedSrc =
    useEmbed && mapUrl
      ? (() => {
          const u = mapUrl.replace(
            /[?&]maptype=(?:satellite|terrain|hybrid|roadmap)/gi,
            ""
          );
          const sep = u.includes("?") ? "&" : "?";
          return u + sep + "maptype=roadmap";
        })()
      : mapUrl;

  const handlePhoneClick = () => {
    trackPhoneClickConversion();
  };

  const addressBoxContent = hasBox ? (
    <div className="w-full max-w-md rounded-xl border border-violet-200 bg-white/95 px-5 py-8 shadow-xl md:max-w-sm md:px-7 md:py-12">
      {box.title != null && String(box.title).trim() !== "" && (
        <h3 className="mb-6 text-[20px] font-bold text-black">
          {box.title.trim()}
        </h3>
      )}
      <div className="space-y-6">
        {box.cityName != null && String(box.cityName).trim() !== "" && (
          <div className="flex gap-4">
            <span className="flex size-10 shrink-0 items-center justify-center text-violet-600" aria-hidden>
              <svg className="size-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
            </span>
            <div>
              <p className="text-base font-semibold uppercase tracking-wide text-black">City</p>
              <p className="mt-1 text-base text-black">{box.cityName.trim()}</p>
            </div>
          </div>
        )}
        {box.address != null && String(box.address).trim() !== "" && (
          <div className="flex gap-4">
            <span className="flex size-10 shrink-0 items-center justify-center text-violet-600" aria-hidden>
              <svg className="size-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
            </span>
            <div>
              <p className="text-base font-semibold uppercase tracking-wide text-black">Address</p>
              <p className="mt-1 whitespace-pre-line text-base leading-relaxed text-black">{box.address.trim()}</p>
            </div>
          </div>
        )}
        {box.phoneNumber != null && String(box.phoneNumber).trim() !== "" && (
          <div className="flex gap-4">
            <span className="flex size-10 shrink-0 items-center justify-center text-violet-600" aria-hidden>
              <svg className="size-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
            </span>
            <div>
              <p className="text-base font-semibold uppercase tracking-wide text-black">Phone</p>
              <a
                href={`tel:${box.phoneNumber.trim().replace(/\s/g, "")}`}
                onClick={handlePhoneClick}
                className="mt-1 block text-base text-black hover:underline"
              >
                {box.phoneNumber.trim()}
              </a>
            </div>
          </div>
        )}
        {box.email != null && String(box.email).trim() !== "" && (
          <div className="flex gap-4">
            <span className="flex size-10 shrink-0 items-center justify-center text-violet-600" aria-hidden>
              <svg className="size-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
            </span>
            <div>
              <p className="text-base font-semibold uppercase tracking-wide text-black">Email</p>
              <a href={`mailto:${box.email.trim()}`} className="mt-1 block text-base text-black hover:underline">{box.email.trim()}</a>
            </div>
          </div>
        )}
        {box.workingHours != null && String(box.workingHours).trim() !== "" && (
          <div className="flex gap-4">
            <span className="flex size-10 shrink-0 items-center justify-center text-violet-600" aria-hidden>
              <svg className="size-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            </span>
            <div>
              <p className="text-base font-semibold uppercase tracking-wide text-black">Working hours</p>
              <p className="mt-1 whitespace-pre-line text-base text-black">{box.workingHours.trim()}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  ) : null;

  return (
    <section
      className="relative w-full overflow-visible pt-4 md:pt-40"
      aria-label="Location and contact"
    >
      {/* Mobile: box first, then map */}
      {hasBox && (
        <div className="mx-auto w-full max-w-md px-4 pb-6 md:hidden">
          {addressBoxContent}
        </div>
      )}

      {/* Full-width map, grayscale, roadmap (street view), interactive – no hover darkening */}
      <div className="relative h-[70vh] min-h-[280px] w-full overflow-visible bg-gray-100">
        <div className="h-full w-full grayscale transition-none [&:hover]:brightness-100 [&:hover]:grayscale">
          {mapUrl && useEmbed ? (
            <iframe
              src={embedSrc}
              title="Location map"
              className="h-full w-full border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          ) : mapUrl ? (
            <a
              href={mapUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex h-full min-h-[280px] flex-col items-center justify-center gap-2 text-center text-violet-700 hover:text-violet-900"
            >
              <span className="text-lg font-semibold">
                View on Google Maps
              </span>
              <span className="text-sm opacity-80">
                Paste an embed URL in Sanity for an inline map
              </span>
            </a>
          ) : (
            <div className="flex h-full min-h-[280px] items-center justify-center text-gray-500">
              Add a Google Map link in Sanity
            </div>
          )}
        </div>

        {/* Desktop: address box overlaid on map */}
        {hasBox && (
          <div className="absolute right-8 top-[11.25rem] hidden -translate-y-1/2 justify-end p-4 md:right-32 md:top-[11.25rem] md:flex md:p-6">
            <div className="backdrop-blur-sm">
              {addressBoxContent}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
